import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { generatePath, useHistory } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'
import { pluralize } from 'utils'
import spinnerImg from './spinner.png'
import { useTutorialModalState } from 'hooks/useTutorialModalState'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import Styles from './GenerationStatusNotification.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useV2GenerationStatus, type V2GenerationStatus } from 'hooks/calendar/useV2GenerationStatus'

const FINISHED_BAR_DURATION_MS = 8000
const FALSE_HYSTERESIS_MS = 1500

export const GenerationStatusNotification = observer(() => {
  const { contentGeneration: store } = useRootStore()
  const { isTutorialModalOpen } = useTutorialModalState()
  const calendarV2 = useFlipperFlag('calendar_v2')
  const v2 = useV2GenerationStatus(calendarV2)

  const [v2FinishedAt, setV2FinishedAt] = useState<number | null>(null)
  const wasActiveRef = useRef(false)
  useEffect(() => {
    if (!calendarV2) return
    if (v2.isActive) {
      wasActiveRef.current = true
      setV2FinishedAt(null)
      return
    }
    if (!wasActiveRef.current) return

    const confirmTimer = setTimeout(() => {
      wasActiveRef.current = false
      setV2FinishedAt(Date.now())
    }, FALSE_HYSTERESIS_MS)
    return () => clearTimeout(confirmTimer)
  }, [calendarV2, v2.isActive])

  useEffect(() => {
    if (v2FinishedAt === null) return
    const t = setTimeout(() => setV2FinishedAt(null), FINISHED_BAR_DURATION_MS)
    return () => clearTimeout(t)
  }, [v2FinishedAt])

  const v2GenerationDone = calendarV2 && v2FinishedAt !== null
  const isVisible = calendarV2 ? v2.isActive || v2GenerationDone : store.isVisible
  const isGenerationDone = calendarV2 ? v2GenerationDone : store.isGenerationDone
  const showGenerationBar = isVisible && !isTutorialModalOpen && !store.shouldYieldToFooter

  if (!showGenerationBar) {
    if (store.calendarNavigationCounts) {
      return (
        <div className={Styles.bar}>
          <CalendarNavButtonLeft />
          <div className={Styles.spacer} />
          <CalendarNavButtonRight />
        </div>
      )
    }
    return null
  }

  if (isGenerationDone) {
    return <FinishedBar />
  }

  return <GeneratingBar v2={calendarV2 ? v2 : null} />
})

const CalendarNavButtonLeft = observer(() => {
  const { contentGeneration: store } = useRootStore()
  const counts = store.calendarNavigationCounts
  if (!counts) return null

  const pastCount = counts.previous_period_count
  const textColor = pastCount === 0 ? 'var(--light-60)' : 'var(--light-100)'

  return (
    <button className={cx(Styles.navButton, Styles.left)} onClick={() => store.calendarOnPrev?.()}>
      <ChevronLeft size={15} color="var(--light-100)" />
      <div className={Styles.textContainer}>
        <Heading level={5} color={textColor}>
          {pastCount}
        </Heading>
        <Text color={textColor}>{pluralize('Post', pastCount)}</Text>
      </div>
    </button>
  )
})

const CalendarNavButtonRight = observer(() => {
  const { contentGeneration: store } = useRootStore()
  const counts = store.calendarNavigationCounts
  if (!counts) return null

  const futureCount = counts.next_period_count
  const textColor = futureCount === 0 ? 'var(--light-60)' : 'var(--light-100)'

  return (
    <button className={cx(Styles.navButton, Styles.right)} onClick={() => store.calendarOnNext?.()}>
      <div className={Styles.textContainer}>
        <Heading level={5} color={textColor}>
          {futureCount}
        </Heading>
        <Text color={textColor}>{pluralize('Post', futureCount)}</Text>
      </div>
      <ChevronRight size={15} color="var(--light-100)" />
    </button>
  )
})

const GeneratingBar = observer(({ v2 }: { v2: V2GenerationStatus | null }) => {
  const { contentGeneration: store } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const elapsed = useElapsedTime(v2 ? v2.startedAtMs : store.startedAt)

  const legacyTotal = store.hasScheduleData ? store.totalCount : store.numberOfNewPosts
  const displayTotal = v2 ? v2.totalCount : legacyTotal
  const completedCount = v2 ? v2.completedCount : store.completedCount
  const showCounter = displayTotal > 0

  return (
    <div className={Styles.bar}>
      <CalendarNavButtonLeft />
      <div className={cx(Styles.barContent, Styles.generating)}>
        <div className={Styles.statusSection}>
          <div className={Styles.spinnerWrap}>
            <img src={spinnerImg} alt="" className={Styles.spinnerImg} />
          </div>
          <span className={Styles.statusLabelWrapper}>
            <span className={Styles.statusLabel}>Generating{!isMobile && ' your content'}</span>
            {!isMobile && <span className={Styles.animatedDots} />}
          </span>
        </div>
        <div className={Styles.rightSection}>
          <div className={Styles.counters}>
            {showCounter && (
              <span className={Styles.countText}>
                {!isMobile && 'Generated '}
                {completedCount}/{displayTotal} {pluralize('post', displayTotal)}
              </span>
            )}
            <span className={Styles.timerText}>{elapsed}</span>
          </div>
        </div>
      </div>
      <CalendarNavButtonRight />
    </div>
  )
})

const FinishedBar = observer(() => {
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()

  const handleGoToContent = () => {
    history.push(generatePath(SCHEDULED_POSTS_PATH, { workspaceId }))
  }

  return (
    <div className={cx(Styles.bar, Styles.finished)}>
      <div className={Styles.finishedContent}>
        <span className={Styles.finishedText}>All content is finished generating.</span>
        <button className={cx(Styles.actionButton, Styles.finishedActionButton)} onClick={handleGoToContent}>
          Go to Content
        </button>
      </div>
    </div>
  )
})

export function useElapsedTime(startedAt: number | null) {
  const [, forceUpdate] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (startedAt !== null) {
      intervalRef.current = setInterval(() => forceUpdate((n) => n + 1), 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [startedAt])

  if (startedAt === null) return '0:00'

  const totalSeconds = Math.floor((Date.now() - startedAt) / 1000)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
